import React, { useState, useRef } from "react";
import { CircularProgress } from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Grid from "@material-ui/core/Grid";
import SignatureCanvas from "react-signature-canvas";
import * as virActions from "src/actions/virActions";
import * as appActions from "src/actions/appActions";
import { base64ToUint8Array } from "src/utils/uploadPhotos";

import generatePdf from "src/utils/generateVirPdf";
import { athenaURL } from "../../env";
import useStyles from "src/layouts/DigitalVIRLayout/useStyles";
import { RoundedButton, Typography } from "@packages/ui";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import getBrandFromUrl from "src/utils/getBrandFromUrl";
import getCountryFromURL from "src/utils/getCountryFromUrl";
import brandColors from "@packages/ui/theme/colors";


const DigitalVIRReviewPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sigCanvas = useRef();
  const [submitting, setSubmitting] = useState(false);
  const brand = getBrandFromUrl().toLowerCase();
  // const [showCanva, setShowCanva] = useState(false);
  const country = getCountryFromURL();
  const canSubmit = customerPresent !== null && !submitting;
  const [customerPresent, setCustomerPresent] = useState(null);
  const [signatureError, setSignatureError] = useState(false);

  const clearSignature = () => {
    sigCanvas.current.clear();
    dispatch(
      appActions.createToast({
        type: "error",
        message: "Please add your signature before submitting.",
      })
    );
  };

  const authorization = useSelector((state) => state.vir.athenaAuth.authorization);
  const damageType = useSelector((state) => state.vir.athenaVirAuth.data.damageType);
  const virPrepareBE = useSelector((state) => state.vir.virPrepareBE);
  const virPrepareTHERMEON = useSelector((state) => state.vir.virPrepareTHERMEON);
  const athenaRole = useSelector((state) => state.vir?.athenaRole?.role);

  const virDetails = useSelector((state) => state.vir?.athenaVirDetail?.data);
  const patchData = useSelector((state) => state.vir?.athenaVirDetailPatch?.data);
  const reportType = useSelector((state) => state.vir?.athenaReportType?.reportType);
  const reviewData = {
    ...virDetails,
    ...patchData,
  };
  const thermeonData = reviewData?.DamageDetails?.DamageCodes || [];
  const selectedVirType = useSelector((state) => state.vir?.athenaSelectedType?.selectedType);
  const selectedVirid = useSelector((state) => state.vir?.athenaSelectedId?.selectedId);
  const resubmitFlag = useSelector((state) => state.vir?.athenaVirResubmit?.flag);
  const source = useSelector((state) => state.vir?.athenaSource?.source);
  const athenaVirAuth = useSelector((state) => state.vir.athenaVirAuth);

  const removeEmptyFields = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(removeEmptyFields).filter((item) => item && Object.keys(item).length > 0);
    } else if (obj !== null && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj)
          .map(([key, value]) => [key, removeEmptyFields(value)])
          .filter(
            ([_, value]) =>
              value !== null &&
              value !== undefined &&
              value !== "" &&
              !(Array.isArray(value) && value.length === 0) &&
              !(typeof value === "object" && Object.keys(value).length === 0)
          )
      );
    }
    return obj;
  };

  const applyPresignedUrl = async (virReportNumber) => {
    const response = await fetch(
      `${athenaURL}/wecapture/get_presigned_url?virId=${selectedVirid}&registration=${virPrepareBE.Registration}
      &rentalType=VirPDF&authorType=${athenaRole}&reportNumber=${virReportNumber}&country=${country}`,
      {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authorization}`,
        },
      }
    );
    const responseData = await response.json();
    return responseData.data;
  };

  const uploadDriverLicence = async ({
    file,
    registration,
    country,
    virId,
    reportNumber,
    zipFilename,
  }) => {
    const formData = new FormData();
    formData.append("file", new Blob([file], { type: "application/zip" }), zipFilename);
    formData.append("registration", registration);
    formData.append("country", country);
    formData.append("virId", virId);
    formData.append("reportNumber", reportNumber);

    const res = await fetch(`${athenaURL}/vir/file`, {
      method: "POST",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${authorization}`,
      },
      body: formData,
    });

    return res;
  };

  const uploadZipFile = async (reportNumber) => {
    const photoBlob = base64ToUint8Array(virPrepareBE.damagePhotos);
    const params = {
      virId: selectedVirid,
      registration: virPrepareBE.Registration,
      unitNumber: athenaVirAuth.data.unitNumber,
      virNumber: reportNumber,
      location: athenaVirAuth.data.currentLocationCode,
      rentalAgreement: athenaVirAuth.data.raNumber,
      author: athenaRole,
      rentalType: athenaRole.toLowerCase() === "customer" ? "CUST_DAMAGE" : "DAMAGE",
      brand: brand,
      country: country,
    };
    const response = await fetch(
      `${athenaURL}/wecapture/get_presigned_url?virId=${params.virId}&virNumber=${reportNumber}&registration=${params.registration}&location=${params.location}&rentalAgreement=${params.rentalAgreement}&unitNumber=${params.unitNumber}&author=${params.author}&rentalType=${params.rentalType}&brand=${params.brand}&country=${params.country}`,
      {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authorization}`,
        },
      }
    );

    const responseData = await response.json();
    const presignedUrl = responseData.data;

    const uploadRes = await fetch(presignedUrl, {
      method: "PUT",
      mode: "cors",
      headers: {
        "Content-Type": "application/zip",
      },
      body: photoBlob,
    });

    if (uploadRes.ok) {
      dispatch(virActions.virCleanUpPhotos());
    } else {
      console.error("Upload failed", uploadRes);
    }
  };

  const pdfProcess = async (
    reviewData,
    params,
    virReportNumber,
    thermeonParam,
    damageType,
    signatureURL
  ) => {
    // generate PDF
    const pdf = generatePdf({
      type: reviewData.VirType,
      damageType: damageType,
      virData: params,
      signatureURL: signatureURL,
      virReportNumber: virReportNumber,
      reportType: reportType,
      customerNumbers: {
        ThirdPartyCustomerNumber: thermeonParam.ThirdPartyCustomerNumber,
        AdditionalThirdParties: thermeonParam.AdditionalThirdParties,
        Witness1Number: thermeonParam.Witness1Number,
        Witness2Number: thermeonParam.Witness2Number,
        OtherDriverNumber: thermeonParam.OtherDriverNumber,
      },
    });

    const presignedUrl = await applyPresignedUrl(virReportNumber);

    await fetch(presignedUrl, {
      method: "PUT",
      mode: "cors",
      body: pdf,
    }).then((result) => {
      if (result.status === 200) {
        dispatch(
          appActions.createToast({ type: "success", message: "VIR damage report PDF is uploaded." })
        );
        navigate(`/vehicleincidents/submissionpage?brand=${brand}`);
      } else {
        dispatch(
          appActions.createToast({ type: "error", message: "VIR damage report PDF upload failed." })
        );
      }
    });
  };

  const createNewVir = async (damageParam, params, damageType, signatureURL) => {
    await fetch(`${athenaURL}/vir/create`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authorization}`,
      },
      mode: "cors",
      method: "POST",
      body: JSON.stringify(damageParam),
    })
      .then((res) => res.json())
      .then(async (data) => {
        if (data.data.ReportNumber) {
          await uploadZipFile(data.data.ReportNumber);
          await pdfProcess(
            reviewData,
            params,
            data.data.ReportNumber,
            data.data.thermeonParam,
            damageType,
            signatureURL
          );
          const baseParam = {
            registration: virPrepareBE.Registration,
            country: country,
            virId: selectedVirid,
            reportNumber: data.data.ReportNumber,
          };
          const prefix =
            damageType === "Rental"
              ? "RA-" + athenaVirAuth.data.raNumber
              : damageType === "Nonrev"
              ? "Nonrev-" + athenaVirAuth.data.nonrevNumber
              : "Unreported";
          if (virPrepareBE.driverLicensePhotos) {
            await uploadDriverLicence({
              ...baseParam,
              file: base64ToUint8Array(virPrepareBE.driverLicensePhotos),
              zipFilename: `${prefix}-VIR-${data.data.ReportNumber}-driverLicenses`,
            });
          }
          setSubmitting(false);
          if (source === "app") {
            navigate(`/vehicleincidents/submissionpage?brand=${brand}`);
          }
        } else {
          setSubmitting(false);
          dispatch(appActions.createToast({ type: "error", message: "VIR create failed." }));
        }
      })
      .catch(() => {
        dispatch(appActions.createToast({ type: "error", message: "Failed to fetch" }));
        setSubmitting(false);
      });
  };

  const updateSubmitedVir = async (damageParam, params, damageType, signatureURL) => {
    try {
      const response = await fetch(`${athenaURL}/vir/create`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authorization}`,
        },
        mode: "cors",
        method: "PUT",
        body: JSON.stringify(damageParam),
      });

      const value = await response.json();

      if (response.ok) {
        await uploadZipFile(value.data.ReportNumber);
        await pdfProcess(
          reviewData,
          params,
          value.data.ReportNumber,
          value.data.thermeonParam,
          damageType,
          signatureURL
        );
        setSubmitting(false);
        if (source === "app") {
          navigate(`/vehicleincidents/submissionpage?brand=${brand}`);
        }
      } else {
        setSubmitting(false);
        dispatch(appActions.createToast({ type: "error", message: "VIR create failed." }));
      }
    } catch (error) {
      dispatch(appActions.createToast({ type: "error", message: "Failed to fetch" }));
      setSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    // Check if signature is present
    const isSignatureEmpty = sigCanvas.current?.isEmpty();
    if ((customerPresent === true || athenaRole === "customer") && isSignatureEmpty) {
      setSignatureError(true);
      dispatch(
        appActions.createToast({
          type: "error",
          message: "Please add your signature before submitting.",
        })
      );
      setSubmitting(false);
      return;
    } else {
      setSignatureError(false);
    }

    let signatureURL = null;
    if (customerPresent === true || athenaRole === "customer") {
      signatureURL = sigCanvas.current.getTrimmedCanvas().toDataURL("image/png");
      dispatch(
        appActions.createToast({
          type: "success",
          message: "Signature saved successfully!",
        })
      );
    }

    const params = {
      data:
        selectedVirType === "windscreen"
          ? {
              Rental: {
                RentalAgreement: athenaVirAuth?.data?.raNumber,
                CustomerNumber: athenaVirAuth?.data?.customerNumber,
                CustomerName: athenaVirAuth?.data?.customerName,
                RentalLocation:
                  athenaVirAuth?.data?.rentalLocation || athenaVirAuth?.data?.checkoutLocation,
                RentalDate: athenaVirAuth?.data?.rentalDate || athenaVirAuth?.data?.checkoutDate,
              },
              Vehicle: {
                Registration: virPrepareBE.Registration,
                Vin: athenaVirAuth?.data?.vin,
                Make: athenaVirAuth?.data?.make,
                Model: athenaVirAuth?.data?.model,
              },
              DamageDetails: { DamageCodes: thermeonData },
              IncidentDetails: {
                Date: reviewData?.IncidentDetails?.Date,
                Description: reviewData?.IncidentDetails?.Description,
                Address: reviewData?.IncidentDetails?.Address,
                RenterDriver: virPrepareTHERMEON?.RenterDriver,
                // CustomerDriving: reviewData?.CustomerDriving,
              },
              AdditionalDetails: reviewData?.AddtionalDetails,
              CustomerDriving: "Y", // For windscreen, always set to Y
              CustomerPresent: athenaRole === "customer" ? "Y" : customerPresent ? "Y" : "N",
            }
          : {
              Rental: {
                RentalAgreement: athenaVirAuth?.data?.raNumber,
                CustomerNumber: athenaVirAuth?.data?.customerNumber,
                CustomerName: athenaVirAuth?.data?.customerName,
                RentalLocation: athenaVirAuth?.data?.rentalLocation,
                RentalDate: athenaVirAuth?.data?.rentalDate,
              },
              Vehicle: {
                Registration: virPrepareBE.Registration,
                Vin: athenaVirAuth?.data?.vin,
                Make: athenaVirAuth?.data?.make,
                Model: athenaVirAuth?.data?.model,
              },
              DamageDetails: { DamageCodes: thermeonData },
              IncidentDetails: {
                Date: reviewData?.IncidentDetails?.Date,
                Description: reviewData?.IncidentDetails?.Description,
                Address: reviewData?.IncidentDetails?.Address,
                RenterDriver: virPrepareTHERMEON?.RenterDriver,
              },
              VehicleConditionAndPoliceDetails: {
                VehicleDrivable: reviewData?.VehicleConditionAndPoliceDetails?.VehicleDrivable,
                VehicleTowed: reviewData?.VehicleConditionAndPoliceDetails?.VehicleTowed,
                TowCompany: reviewData?.VehicleConditionAndPoliceDetails?.TowCompany,
                PoliceAttended: reviewData?.VehicleConditionAndPoliceDetails?.PoliceAttended,
                Police: {
                  PoliceReportNumber:
                    reviewData?.VehicleConditionAndPoliceDetails?.Police?.PoliceReportNumber,
                  PoliceName: reviewData?.VehicleConditionAndPoliceDetails?.Police?.PoliceName,
                  PoliceStation:
                    reviewData?.VehicleConditionAndPoliceDetails?.Police?.PoliceStation,
                },
                HasInsurance: reviewData?.VehicleConditionAndPoliceDetails?.HasInsurance,
                Insurance: {
                  InsuranceNumber:
                    reviewData?.VehicleConditionAndPoliceDetails?.Insurance?.InsuranceNumber,
                  InsuranceProvider:
                    reviewData?.VehicleConditionAndPoliceDetails?.Insurance?.InsuranceProvider,
                },
              },
              ThirdPartyDetail: reviewData?.ThirdPartyDetail,
              Witnesses: reviewData?.Witnesses,
              Witness1: reviewData?.Witness1,
              Witness2: reviewData?.Witness2,
              AdditionalThirdPartyDetails: reviewData?.AdditionalThirdPartyDetails,
              AdditionalDetails: reviewData?.AddtionalDetails,
              // if staff, customerPresent will measures whether the customer was physically with them while they did the VIR
              CustomerPresent:
                athenaRole === "customer"
                  ? reviewData?.CustomerPresent
                  : customerPresent
                  ? "Y"
                  : "N",
              CustomerDriving: reviewData?.CustomerDriving,
            },
    };

    if (
      reviewData.IncidentDetails?.RenterDriver !== "Y" &&
      selectedVirType !== "windscreen" &&
      reviewData?.OtherDriverDetails
    ) {
      params.data.OtherDriverDetails = {
        FirstName: reviewData?.OtherDriverDetails?.FirstName,
        LastName: reviewData?.OtherDriverDetails?.LastName,
        DrivingLicenceNumber: reviewData?.OtherDriverDetails?.DrivingLicenceNumber,
        DrivingLicenceExpiryDate: reviewData?.OtherDriverDetails?.DrivingLicenceExpiryDate,
        CellTelephoneNumber: reviewData?.OtherDriverDetails?.CellTelephoneNumber,
        Email: reviewData?.OtherDriverDetails?.Email,
        Address: reviewData?.OtherDriverDetails?.Address,
        Relationship: reviewData?.OtherDriverDetails?.Relationship,
        OtherRelationship: reviewData?.OtherDriverDetails?.OtherRelationship,
        DriverLicenceUploaded: reviewData?.OtherDriverDetails?.DriverLicenceUploaded,
      };
    }

    if (damageType === "Nonrev") {
      params.data.Rental.NonrevNumber = athenaVirAuth?.data?.nonRevNumber;
      params.data.Rental.RentalLocation = athenaVirAuth?.data?.checkoutLocation;
      params.data.Rental.RentalDate = athenaVirAuth?.data?.checkoutDate;
    }

    // Remove empty fields
    params.data = removeEmptyFields(params.data);
    const damageParam = {
      DrivingLicence: virPrepareBE.Registration,
      virId: selectedVirid,
      virStatus: "ReviewPage",
      data: params.data,
    };
    console.log("reviewData", reviewData);
    console.log("params", params);
    if (!resubmitFlag) {
      await createNewVir(damageParam, params, damageType, signatureURL);
    } else {
      await updateSubmitedVir(damageParam, params, damageType, signatureURL);
    }
  };

  return (
    <>
      <Box
        sx={{
          textAlign: "left",
          fontFamily: "Ride",
          color: "#333",
          maxWidth: "500px",
          margin: "0 auto",
        }}
      >
        <h1 className={classes.heading}>Please review the incident details and sign.</h1>
        <hr className={classes.breakLine} style={{ borderColor: brandColors[brand] }} />
        <br></br>
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            fontFamily: "Ride",
            color: "#333",
            maxWidth: "500px",
            margin: "0 auto",
          }}
        >
          {/* ...existing details grid... */}

          <Grid
            container
            spacing={2}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontFamily: "Ride",
            }}
          >
            <Grid item xs={16}>
              <Typography className={classes.descripe} color="textPrimary">
                <b> Date and Time:</b> {patchData?.IncidentDetails?.Date.replace("T", " ")}
              </Typography>
            </Grid>
            {/* ...other details... */}
          </Grid>

          {/* Mandatory customer present question */}
          {athenaRole === "staff" && source === "app" && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                color: "#333",
                textAlign: "center",
                margin: "0 auto",
              }}
            >
              <Typography
                style={{
                  fontFamily: "Ride",
                  fontSize: "18px",
                  fontWeight: "bold",
                  marginBottom: "10px",
                  color: "#333",
                }}
              >
                Is the customer present?
                <span style={{ color: "red", marginLeft: 4 }}>*</span>
              </Typography>
              <Box display="flex" justifyContent="center" alignItems="center" gap={4}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    marginRight: "20px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={customerPresent === true}
                    onChange={() => setCustomerPresent(true)}
                  />
                  <span style={{ marginLeft: 8 }}>Yes</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={customerPresent === false}
                    onChange={() => setCustomerPresent(false)}
                  />
                  <span style={{ marginLeft: 8 }}>No</span>
                </label>
              </Box>
              {customerPresent === null && (
                <Typography color="error" style={{ fontSize: "13px", marginTop: "8px" }}>
                  Please answer this question to proceed.
                </Typography>
              )}
            </Box>
          )}

          {/* Signature panel only if Yes */}
          {(customerPresent === true || athenaRole === "customer") && (
            <Box className={classes.signatureContainer}>
              <Typography variant="h6" gutterBottom>
                PLEASE SIGN BELOW
              </Typography>
              <Box className={classes.signatureBox}>
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="black"
                  canvasProps={{
                    width: 500,
                    height: 200,
                    className: "sigCanvas",
                  }}
                />
                <IconButton onClick={clearSignature} className={classes.IconButton}>
                  <HighlightOffIcon />
                </IconButton>
                <div className={classes.horizontalLine}></div>
              </Box>
              {signatureError && (
                <Typography color="error" style={{ fontSize: "13px", marginTop: "8px" }}>
                  Please sign in the panel before submitting.
                </Typography>
              )}
              <Box className={classes.buttonContainer}></Box>
            </Box>
          )}
        </Box>
        <Box sx={{ my: 3 }}>
          <RoundedButton
            onClick={handleSubmit}
            variant="contained"
            fullWidth
            sx={{ mt: 2, width: "80%", margin: "2" }}
            disabled={(customerPresent === null && athenaRole === "staff") || submitting}
          >
            {!submitting ? "Submit Form" : <CircularProgress />}
          </RoundedButton>
        </Box>
      </Box>
    </>
  );
};

export default DigitalVIRReviewPage;
